import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CeoEmailMessageI, CeoEmailsMetaI } from 'src/app/interfaces/ceo-emails.interface';

/**
 * ⭐ WHO THE EMAIL COMES FROM — any address on totalum.app, and a reply-to from a
 * closed list.
 *
 * ⚠️ THE TWO HALVES ARE NOT THE SAME FREEDOM, on purpose:
 *   · FROM is free (`news@`, `hello@`, `francesc@`…). The DOMAIN is what SendGrid
 *     authenticates, so every address on it signs with our DKIM.
 *   · REPLY-TO is francesc@ / joanpol@ / contacto@ — or nothing at all. A reply only
 *     helps if it reaches a mailbox somebody reads; anything else loses the answer.
 *
 * It edits the message object in place and emits `changed`, like the other builders.
 */
@Component({
  selector: 'app-ceo-sender-picker',
  templateUrl: './ceo-sender-picker.component.html',
})
export class CeoSenderPickerComponent {
  @Input() message!: CeoEmailMessageI;
  @Input() meta: CeoEmailsMetaI | null = null;
  @Input() compact = false;
  @Output() changed = new EventEmitter<void>();

  customOpen = false;

  get domain(): string {
    return this.meta?.emailDomain || 'totalum.app';
  }

  get localPart(): string {
    return (this.message?.fromEmail || '').split('@')[0] || '';
  }

  set localPart(value: string) {
    const local = (value || '').trim().toLowerCase().split('@')[0];
    this.message.fromEmail = local ? `${local}@${this.domain}` : '';
    this.changed.emit();
  }

  isPreset(email: string): boolean {
    return this.message?.fromEmail === email;
  }

  /** True when the address is not one of the one-click presets. */
  get isCustom(): boolean {
    const presets = this.meta?.senderPresets || [];
    return !!this.message?.fromEmail && !presets.some(p => p.email === this.message.fromEmail);
  }

  usePreset(preset: { email: string; name: string }): void {
    this.message.fromEmail = preset.email;
    this.message.fromName = preset.name;
    // A preset replies to itself where that address can receive mail; noreply@ cannot.
    const local = preset.email.split('@')[0];
    const allowed = (this.meta?.replyToOptions || []).some(o => o.key === local);
    this.message.replyToKey = allowed ? local : '';
    this.customOpen = false;
    this.changed.emit();
  }

  openCustom(): void {
    this.customOpen = true;
    if (!this.isCustom) {
      this.message.fromEmail = '';
      this.message.fromName = '';
      this.changed.emit();
    }
  }

  onFieldChange(): void {
    this.changed.emit();
  }

  get replyToLabel(): string {
    const option = (this.meta?.replyToOptions || []).find(o => o.key === (this.message?.replyToKey || ''));
    return option?.email || 'nobody';
  }
}
